import { Link } from "@/components/LangLink";
import { ArrowUpRight } from "lucide-react";
import { PageHero, CTASection, CheckList, SolutionsShowcase, SectionHeading } from "@/components/sections";
import { Reveal } from "@/components/primitives";
import { IMG } from "@/lib/site";

const SECTIONS = [
  {
    id: "preventive",
    n: "01",
    kicker: "Calze Preventive",
    brand: "LUMAVEN",
    brandTheme: "green",
    to: "/prodotti/lumaven",
    img: IMG.walking,
    title: "Prevenzione quotidiana, comfort discreto.",
    desc: "Per chi passa molte ore in piedi o seduto, in gravidanza o in viaggio. Un sostegno leggero che aiuta la circolazione senza rinunciare al comfort.",
    points: ["Riduzione del senso di pesantezza", "Prevenzione del ristagno venoso", "Vestibilità elegante e discreta"],
  },
  {
    id: "terapeutiche",
    n: "02",
    kicker: "Calze Terapeutiche · Curative",
    brand: "SCHIEBLER",
    brandTheme: "navy",
    to: "/prodotti/schiebler",
    img: IMG.schiebler,
    title: "Compressione medicale certificata.",
    desc: "Il cuore della terapia: calze curative SCHIEBLER per il trattamento dell'insufficienza venosa cronica e del linfedema, con classi di compressione certificate.",
    points: ["STAR COTTON — comfort in cotone", "GOLDPUNKT KARAT — la linea di riferimento", "DOKTUS — precisione terapeutica"],
    highlight: true,
  },
  {
    id: "antitrombo",
    n: "03",
    kicker: "Calze Antitrombo",
    brand: "LUMAVEN",
    brandTheme: "green",
    to: "/prodotti/lumaven",
    img: IMG.lumaven,
    title: "Profilassi antitromboembolica.",
    desc: "Per il paziente allettato o nel percorso post-operatorio: calze antitrombo LUMAVEN per la prevenzione della trombosi venosa profonda, in ospedale e a domicilio.",
    points: ["Prevenzione TVP", "Uso ospedaliero e domiciliare", "Massima sicurezza clinica"],
  },
  {
    id: "bendaggi",
    n: "04",
    kicker: "Bendaggi",
    brand: "Bendaggi",
    badge: "Categoria",
    brandTheme: "navy",
    to: "/prodotti/bendaggi",
    img: IMG.fabric,
    title: "Bendaggi elastici per la fase intensiva.",
    desc: "Bende elastiche WERO SWISS a corta e lunga estensibilità per il trattamento decongestivo: alta pressione di lavoro e comfort a riposo, ideali per il linfedema e l'insufficienza venosa.",
    points: ["Corta e lunga estensibilità", "Ideali per il linfedema", "Applicazione versatile e riutilizzabile"],
  },
  {
    id: "pressoterapia",
    n: "05",
    kicker: "Pressoterapia",
    brand: "Pressoterapia",
    badge: "Categoria",
    brandTheme: "navy",
    to: "/prodotti/pressoterapia",
    img: IMG.pressotherapy,
    title: "Drenaggio linfatico sequenziale.",
    desc: "Sistemi di pressoterapia LEM a 4 uscite con programmi sequenziali A/B: la compressione si muove dalla sezione distale alla prossimale favorendo il drenaggio, con pressione regolabile dal display touch screen.",
    points: ["4 uscite sequenziali", "Programmi A e B", "Pressione regolabile 20–250 mmHg"],
  },
];

const Soluzioni = () => (
  <>
    <PageHero
      eyebrow="Soluzioni"
      title={"Ogni bisogno\nha la sua soluzione."}
      subtitle="Dalla prevenzione alla terapia avanzata: cinque soluzioni per ogni fase del percorso di cura."
      testid="soluzioni-hero"
    />

    {/* Showcase: 3 calze in evidenza + complementi discreti */}
    <section className="py-20 lg:py-28 bg-white" data-testid="soluzioni-showcase">
      <div className="container-x">
        <SectionHeading eyebrow="Le nostre soluzioni" title="Le calze sono il cuore della nostra offerta." intro="Il fulcro della terapia elasto-compressiva, con bendaggi e pressoterapia a completamento del percorso." />
        <div className="mt-14">
          <SolutionsShowcase />
        </div>
      </div>
    </section>

    {SECTIONS.map((s, i) => {
      const reversed = i % 2 === 1;
      return (
        <section
          key={s.id}
          id={s.id}
          className={`py-20 lg:py-28 scroll-mt-24 ${i % 2 === 0 ? "bg-cloud" : "bg-white"}`}
          data-testid={`soluzione-${s.id}`}
        >
          <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
            <Reveal className={reversed ? "lg:order-2" : ""}>
              <div className="relative overflow-hidden rounded-[1.75rem] border border-[#E7EDF2] shadow-[0_30px_70px_-30px_rgba(15,25,35,0.22)]">
                <img src={s.img} alt={s.title} className="w-full h-[440px] object-cover" />
                <span className={`absolute top-5 left-5 rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white ${s.brandTheme === "green" ? "bg-green-deep" : "bg-navy-deep"}`}>
                  {s.badge || s.brand}
                </span>
              </div>
            </Reveal>
            <div className={reversed ? "lg:order-1" : ""}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-serif text-5xl font-bold text-green/20">{s.n}</span>
                <span className="eyebrow">{s.kicker}</span>
                {s.highlight && (
                  <span className="rounded-full bg-green/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-green-deep">La più scelta</span>
                )}
              </div>
              <Reveal delay={0.05}>
                <h2 className="mt-4 font-serif text-3xl lg:text-4xl font-bold text-navy leading-tight">{s.title}</h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{s.desc}</p>
              </Reveal>
              <Reveal delay={0.15}>
                <CheckList items={s.points} className="mt-6" />
              </Reveal>
              <Reveal delay={0.2}>
                <Link to={s.to} data-testid={`soluzione-link-${s.id}`} className="mt-8 btn-green inline-flex">
                  Vedi i prodotti {s.brand.split(" ")[0]} <ArrowUpRight size={16} />
                </Link>
              </Reveal>
            </div>
          </div>
        </section>
      );
    })}

    <CTASection />
  </>
);

export default Soluzioni;
